// @wc-ignore-file

import type { CrClassification } from "@shared/types/supabase.types";
import { CR_CLASSIFICATIONS } from "./change-requests.constants";

/**
 * Parse the `type` search param of the CR board route (CR-118) into a
 * validated multi-select, exactly like `parsePrioritySearch`: an array or a
 * bare string (hand-typed `?type=fix`), unknown values and duplicates dropped,
 * `undefined` — "no filter" — when nothing valid remains.
 */
export function parseClassificationSearch(value: unknown): CrClassification[] | undefined {
  const candidates = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];

  const valid = CR_CLASSIFICATIONS.filter((classification) => candidates.includes(classification));
  return valid.length > 0 ? valid : undefined;
}

/**
 * Client-side board filter. No selection shows every CR, including those with
 * no type (`classification: null`); as soon as any type is selected, untyped
 * CRs are hidden — unlike priority "none", there is no "no type" value to pick.
 */
export function filterCrsByClassification<T extends { classification: CrClassification | null }>(
  crs: T[],
  selected: CrClassification[] | undefined,
): T[] {
  if (!selected || selected.length === 0) return crs;
  return crs.filter((cr) => cr.classification !== null && selected.includes(cr.classification));
}

/** Toggle one classification in the current selection, normalising back to
 * `undefined` when the selection empties. */
export function toggleClassification(
  selected: CrClassification[] | undefined,
  classification: CrClassification,
): CrClassification[] | undefined {
  const current = selected ?? [];
  const next = current.includes(classification)
    ? current.filter((c) => c !== classification)
    : // Keep CR_CLASSIFICATIONS order regardless of click order.
      CR_CLASSIFICATIONS.filter((c) => current.includes(c) || c === classification);
  return next.length > 0 ? next : undefined;
}
